import { useEffect } from 'react';
import merge from 'classnames';
import { filter, sortBy, sum, sumBy } from 'lodash';

import { BomLink } from '@/components/generic';
import { useOrderContext } from '@/components/modals/SubmitOrder/helpers';
import { ProposalWithBom } from '@/controllers/Activity';
import { useDeviceDetection } from '@/hooks/useDeviceDetection';
import { ContactVm, MediaType, OrderInventoryType } from '@/types';
import { formatPriceWithCurrency } from '@/utils/format-price';

import {
  getOrderItemName,
  getOrderItemPrice,
  getOrderTotal,
  getShippingQuantity,
  getTaxableOrderItem,
  isAddonProduct,
  isAsphaltShingle,
  isEquipment,
  isInventory,
  isStorageProduct,
  isWarranty,
  productHasZeroQuantity,
} from '../../../../constants';
import { OrderItemWrapper } from '../../../../types';
import { OrderDetails } from '../../../Common/OrderDetails';
import { ProposalBlockWrapper } from '../../../Common/ProposalBlockWrapper';
import { isProductAvailable } from '../../../OrderDetailsStep/components/SelectServices/constants';
import { getNonEquipmentItems, TIMBERLINE_PRODUCT_NAME } from '../../helpers';
import { ConfirmationContentDetails } from '../ConfirmationContentDetails';

import { Props } from './types';

import styles from '../../ConfirmationStep.module.scss';

export const ConfirmationOrderDetails = ({
  calculateTax,
  calculatedTax,
  goToStep,
  isActive,
  productLineName,
  proposal,
  bomTotal,
}: Props) => {
  const state = useOrderContext();
  const isActivityDistributionModel = state.isDistribution;
  const mediaType = useDeviceDetection();

  const goToOrderDetails = () => {
    goToStep?.();
  };

  const shouldShowInventoryItem = (oi: OrderItemWrapper) =>
    state.order.inventoryType !== OrderInventoryType['Need Inventory'] &&
    isEquipment(oi) &&
    isInventory(oi) &&
    productLineName !== 'Decotech®';

  const equipment = filter(
    state.visibleOrderItems,
    oi =>
      !productHasZeroQuantity(oi) &&
      ((isEquipment(oi) && !isInventory(oi)) ||
        isStorageProduct(oi.priceBookEntry) ||
        shouldShowInventoryItem(oi)),
  );
  const addons = filter(
    state.visibleOrderItems,
    oi => isAddonProduct(oi) && isProductAvailable(oi) && !productHasZeroQuantity(oi),
  );

  const proposalId = proposal?.id;
  const isSkuEnable = bomTotal !== null;
  const hasAsphaltShingleColor = (oi: OrderItemWrapper) => {
    return Boolean(oi.priceBookEntry?.product?.asphaltShingleColor);
  };

  let sortedEquipment = sortBy([...equipment, ...addons], e => e.priceBookEntry.product.type);
  let estimatedSkuCost = 0;

  if (isSkuEnable) {
    sortedEquipment = filter(
      sortedEquipment,
      oi =>
        isAsphaltShingle(oi) || isStorageProduct(oi.priceBookEntry) || hasAsphaltShingleColor(oi),
    );
    estimatedSkuCost = (proposal as ProposalWithBom)?.estimatedSolarMaterialsCost || 0;
  }

  const services = getNonEquipmentItems(state);

  const warranties = filter(state.visibleOrderItems, oi => isWarranty(oi.priceBookEntry));

  const equipmentTotal = sumBy(sortedEquipment, oi => getOrderItemPrice(state, oi, proposal));
  const servicesTotal = sumBy(services, oi => getOrderItemPrice(state, oi, proposal));
  const warrantiesTotal = sumBy(warranties, oi => getOrderItemPrice(state, oi, proposal));
  const orderTotal = isSkuEnable
    ? Number(bomTotal)
    : getOrderTotal(state, productLineName, proposal);
  const finalTotal = orderTotal + (calculatedTax ?? 0);
  const shouldDisplayEquipmentList = productLineName === TIMBERLINE_PRODUCT_NAME && sortedEquipment;
  const estimatedTaxesTitle = mediaType === MediaType.Mobile ? 'Est. Taxes' : 'Estimated Taxes';

  useEffect(() => {
    if (!isActivityDistributionModel && isActive && orderTotal > 0) {
      const OrderItems = [...equipment, ...addons, ...services, ...warranties];
      const taxableOrderItems = OrderItems.map(taxableItem =>
        getTaxableOrderItem(state, taxableItem, proposal),
      );

      calculateTax(state.order.shipmentContact as ContactVm, taxableOrderItems, proposalId ?? '');
    }
  }, [isActive]);

  if (isActivityDistributionModel) {
    return null;
  }

  const title = isSkuEnable ? 'Total Solar and Roofing Materials Cost' : productLineName;
  const totalSolarCost = isSkuEnable ? sum([estimatedSkuCost, equipmentTotal]) : equipmentTotal;

  return (
    <ProposalBlockWrapper
      link={{
        label: 'Edit Order',
        click: goToOrderDetails,
      }}
      title="Order details"
    >
      <OrderDetails hideValue={state.isPriceHidden} title={title} value={totalSolarCost}>
        {isSkuEnable && (
          <div className={styles.order_list_body_item}>
            <div className={styles.order_list_body_raw}>
              <div className={merge(styles.order_list_body_raw, styles.equipment_item_title)}>
                <BomLink fontSize={16} proposalId={proposalId} />
              </div>
              <span className={styles.order_list_body_item_bold}>
                {!state.isPriceHidden && formatPriceWithCurrency(estimatedSkuCost)}
              </span>
            </div>
          </div>
        )}
        {shouldDisplayEquipmentList &&
          sortedEquipment.map((equipment: OrderItemWrapper) => (
            <div className={styles.order_list_body_item} key={equipment.orderItem.productId}>
              <div className={styles.order_list_body_raw}>
                <div className={merge(styles.order_list_body_raw, styles.equipment_item_title)}>
                  <span className={styles.order_list_body_item_bold}>
                    {getOrderItemName(equipment)}
                  </span>
                  {hasAsphaltShingleColor(equipment) && (
                    <>
                      <span className={styles.vertical_divider}>{' | '}</span>
                      <span
                        className={merge(
                          styles.order_list_body_item_light,
                          styles.equipment_shingle_color,
                        )}
                      >
                        {equipment.priceBookEntry.product.asphaltShingleColor}
                      </span>
                    </>
                  )}
                </div>
                {!state.isPriceHidden && (
                  <span className={styles.order_list_body_item_bold}>
                    {formatPriceWithCurrency(getOrderItemPrice(state, equipment, proposal))}
                  </span>
                )}
              </div>
              <hr className={styles.horizontal_divider} />
              {!isInventory(equipment) && (
                <div
                  className={merge(styles.order_list_body_raw, styles.order_list_quantity_raw)}
                  style={{ justifyContent: 'flex-start' }}
                >
                  <span className={styles.order_list_body_item_bold}>Quantity: &nbsp;</span>
                  {!productHasZeroQuantity(equipment) && (
                    <span className={styles.order_list_body_item_light}>
                      {getShippingQuantity(equipment)}
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
      </OrderDetails>

      <ConfirmationContentDetails
        itemsToRender={services}
        itemsTotal={servicesTotal}
        proposal={proposal}
        title="Services"
      />

      {warranties.length > 0 && (
        <OrderDetails hideValue={state.isPriceHidden} title="Warranties" value={warrantiesTotal}>
          {warranties.map((item: OrderItemWrapper) => (
            <div className={styles.order_list_body_item} key={item.orderItem.productId}>
              <div className={styles.order_list_body_raw}>
                <span className={styles.order_list_body_item_bold}>{getOrderItemName(item)}</span>
                {!state.isPriceHidden && (
                  <span className={styles.order_list_body_item_bold}>
                    {formatPriceWithCurrency(getOrderItemPrice(state, item, proposal))}
                  </span>
                )}
              </div>
            </div>
          ))}
        </OrderDetails>
      )}

      {!state.isPriceHidden && (
        <>
          <OrderDetails title={estimatedTaxesTitle} value={calculatedTax ?? 0} />
          <div className={styles.order_total}>
            <span>Total Costs:</span>
            <span>{formatPriceWithCurrency(finalTotal)}</span>
          </div>
        </>
      )}
    </ProposalBlockWrapper>
  );
};
